import * as React from 'react';
import { cn } from '@/lib/utils/cn';

/**
 * Portfolio KPI tile strip (T-0107 / C20-130).
 *
 * Renders five tiles across the top of the portfolio page. Visual contract
 * mirrors `design_handoff_caliber_v2/prototype/screens-portfolio.js → kpi()`
 * — paper-0 background, ink-100 border, 8px radius, 16/20px padding, with
 * the trend sub-line carrying coloured spans for healthy/at-risk/critical
 * breakdowns.
 *
 * Every tile renders an explicit "Pending — backend gap" trend line if the
 * source query is still pending or no backend source exists today (per the
 * C20-130 requirement that every element either consume real data or
 * explicitly surface a pending state — never seeded mock data).
 */

export interface PortfolioKpiTile {
  label: string;
  /** Headline value. Pass `null` to render an em-dash pending placeholder. */
  value: React.ReactNode | null;
  /** Optional sub-line — accepts JSX so multi-color breakdowns are possible. */
  trend?: React.ReactNode;
  /** When true, the value renders ink-300 + emphasises the pending nature. */
  pending?: boolean;
}

export interface PortfolioKpiStripProps extends React.HTMLAttributes<HTMLDivElement> {
  tiles: ReadonlyArray<PortfolioKpiTile>;
}

export function PortfolioKpiStrip({ tiles, className, ...rest }: PortfolioKpiStripProps) {
  return (
    <div
      className={cn('flex flex-wrap items-stretch gap-3', className)}
      role="group"
      aria-label="Portfolio KPIs"
      {...rest}
    >
      {tiles.map((tile, idx) => (
        <div
          key={`${tile.label}-${idx}`}
          className="flex min-w-[180px] flex-1 flex-col gap-1.5 rounded-card border border-ink-100 bg-paper-0 px-5 py-4"
        >
          <div className="text-meta font-medium text-ink-500">{tile.label}</div>
          <div
            className={cn(
              'text-[24px] font-semibold leading-[30px] tabular-nums tracking-[-0.01em]',
              tile.pending || tile.value == null ? 'text-ink-300' : 'text-ink-900',
            )}
          >
            {tile.value == null ? '—' : tile.value}
          </div>
          {tile.trend ? (
            <div className="flex items-center gap-1.5 text-meta text-ink-500">{tile.trend}</div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

PortfolioKpiStrip.displayName = 'PortfolioKpiStrip';
